import { useState } from 'react';
import { CalendarCustomization } from '../types';
import { CalendarCustomizerProps } from '../types';
import { CalendarLocationSection } from './calendar/CalendarLocationSection';


const defaultLocationState = {
  address: '',
  city: '',
  state: '',
  zip_code: '',
  agency_combine: '',
  additional_agents: '',
  phone: '',
  cell_phone: '',
  fax: '',
  other_info: '',
  textColor: '#000000',
  formScale: 0.5,
  formOpacity: 0.2
};

const DEFAULT_POSITION = { x: 0, y: 0 };

export function CalendarCustomizer({ onCustomizationChange, initialCustomization, previewImageUrl, style, fields, withBorder = true }: CalendarCustomizerProps) {
  const [showSecondary, setShowSecondary] = useState(!!initialCustomization?.locations.secondary);
  const [showTertiary, setShowTertiary] = useState(!!initialCustomization?.locations.tertiary);

  const [primaryLocation, setPrimaryLocation] = useState(
    initialCustomization?.locations.primary || defaultLocationState
  );
  const [secondaryLocation, setSecondaryLocation] = useState(
    initialCustomization?.locations.secondary || defaultLocationState
  );
  const [tertiaryLocation, setTertiaryLocation] = useState(
    initialCustomization?.locations.tertiary || defaultLocationState
  );

  const updateCustomization = (
    primary: typeof primaryLocation,
    secondary?: typeof secondaryLocation,
    tertiary?: typeof tertiaryLocation
  ) => {
    const customization: CalendarCustomization = {
      locations: {
        primary: { ...primary, formPosition: DEFAULT_POSITION },
        ...(secondary && showSecondary ? { secondary: { ...secondary, formPosition: DEFAULT_POSITION } } : {}),
        ...(tertiary && showTertiary ? { tertiary: { ...tertiary, formPosition: DEFAULT_POSITION } } : {})
      }
    };
    onCustomizationChange(customization);
  };

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const processFieldValue = (field: string, value: string): string | number => {
    if (field === 'phone' || field === 'cell_phone' || field === 'fax') {
      return formatPhoneNumber(value);
    }
    if (field === 'formScale' || field === 'formOpacity') {
      return parseFloat(value);
    }
    return value;
  };

  const handlePrimaryChange = (field: string, value: string) => {
    const updated = { ...primaryLocation, [field]: processFieldValue(field, value) };
    setPrimaryLocation(updated);
    updateCustomization(updated, secondaryLocation, tertiaryLocation);
  };

  const handleSecondaryChange = (field: string, value: string) => {
    const updated = { ...secondaryLocation, [field]: processFieldValue(field, value) };
    setSecondaryLocation(updated);
    updateCustomization(primaryLocation, updated, tertiaryLocation);
  };

  const handleTertiaryChange = (field: string, value: string) => {
    const updated = { ...tertiaryLocation, [field]: processFieldValue(field, value) };
    setTertiaryLocation(updated);
    updateCustomization(primaryLocation, secondaryLocation, updated);
  };

  return (
    <div className="space-y-8">
        <CalendarLocationSection
          title="Primary Location Calendar"
          imageUrl={previewImageUrl || ''}
          location={primaryLocation}
          altText="Primary Calendar"
          style={style}
          fields={fields}
          withBorder={withBorder}
          onFieldChange={handlePrimaryChange}
        />

        {showSecondary && (
          <CalendarLocationSection
            title="Second Location Calendar"
            imageUrl={previewImageUrl || ''}
            location={secondaryLocation}
            altText="Secondary Calendar"
            showRemove
            style={style}
            fields={fields}
            withBorder={withBorder}
            onFieldChange={handleSecondaryChange}
            onRemove={() => setShowSecondary(false)}
          />
        )}

        {showTertiary && (
          <CalendarLocationSection
            title="Third Location Calendar"
            imageUrl={previewImageUrl || ''}
            location={tertiaryLocation}
            altText="Tertiary Calendar"
            showRemove
            style={style}
            fields={fields}
            withBorder={withBorder}
            onFieldChange={handleTertiaryChange}
            onRemove={() => setShowTertiary(false)}
          />
        )}

      {!showSecondary && (
        <button
          onClick={() => setShowSecondary(true)}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          + Add Second Location Calendar
        </button>
      )}

      {showSecondary && !showTertiary && (
        <button
          onClick={() => setShowTertiary(true)}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          + Add Third Location Calendar
        </button>
      )}
    </div>
  );
}
